import { Component, computed, effect, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterOutlet, RouterLink, Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { LiveChatWidgetComponent } from './features/live-chat/live-chat-widget.component';
import { AddJobComponent } from './features/add-job/add-job.component';
import { SessionExpiredBannerComponent } from './shared/components/session-expired-banner/session-expired-banner.component';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, RouterLink, AddJobComponent, LiveChatWidgetComponent, SessionExpiredBannerComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App {
  private readonly router = inject(Router);

  protected readonly title = signal('rfi-admin-portal-v2');

  /** Set true on first click of "Add Job"; keeps the `@defer` block's component code out of the
   *  eager bundle until an admin actually wants to create a job.
   *
   *  `addJobModal` is queried by the `#addJobRef` template reference variable rather than
   *  `viewChild(AddJobComponent)` — passing the class itself as the locator would be a runtime
   *  reference to `AddJobComponent` from this eagerly-loaded component, which defeats Angular's
   *  automatic `@defer`-import optimization (it only lazy-loads a component when the only
   *  reference to its class is the `imports` array feeding the `@defer` block in the template). */
  readonly addJobDeferred = signal(false);
  private readonly addJobModal = viewChild<AddJobComponent>('addJobRef');
  private readonly addJobOpenRequests = signal(0);

  openAddJob(): void {
    this.addJobDeferred.set(true);
    this.addJobOpenRequests.update((n) => n + 1);
  }

  /** Notes & Activity is embedded from legacy admin — hide global chrome for a full-page experience.
   *  Also hide for distant-vendor-approval pages (accessed via email link), for the public
   *  forgot-password page (reached from the legacy sign-in screen, no session), and for Accounting
   *  V2 screens, which ship their own topbar/tab-bar matching complete-screen-v2.html exactly. */
  readonly hideAppChrome = signal(
    // router.url is still "/" before the first navigation; the frame must never show V2's header.
    this.shouldHideChrome(this.router.url) ||
      window.location.pathname.toLowerCase().startsWith('/embed/chat') ||
      window.location.pathname.toLowerCase().startsWith('/chat-window') ||
      this.isLibraryRoute(window.location.pathname.toLowerCase()),
  );

  /** Seeded from the browser location rather than `router.url`: on a direct load the router still
   *  reports `/` until the first navigation settles, which would briefly mount the chat widget (and
   *  fire its API calls) on a public page before `NavigationEnd` corrects it. */
  private readonly currentRoute = signal(window.location.pathname || this.router.url);

  /** Routes reachable with no session at all (e.g. `/forgot-password`): no chat widget, nothing
   *  that would call a JWT-protected endpoint on behalf of a user who isn't signed in. */
  readonly isPublicPage = computed(
    () =>
      this.currentRoute().includes('/forgot-password') ||
      this.currentRoute().includes('/onsite-approval-override/') ||
      this.isLibraryRoute(this.currentRoute()),
  );


  constructor() {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((event) => {
        this.hideAppChrome.set(this.shouldHideChrome(event.urlAfterRedirects));
        this.currentRoute.set(event.urlAfterRedirects);
      });

    effect(() => {
      const modal = this.addJobModal();
      if (modal && this.addJobOpenRequests() > 0) {
        modal.open();
      }
    });
  }

  /** The Job Story Library and the vendor "Show us the win" page are separate apps inside this build: no Admin header,
   *  no job chat, no Admin session banner. */
  private isLibraryRoute(url: string): boolean {
    return (
      url === '/library' || url.startsWith('/library/') || url.startsWith('/library?') ||
      url.startsWith('/story/')
    );
  }

  private shouldHideChrome(url: string): boolean {
    return (
      this.isLibraryRoute(url) ||
      url.startsWith('/embed/chat') ||
      url.startsWith('/chat-window') ||
      url.includes('/distant-vendor-approval') ||
      url.includes('/forgot-password') ||
      url.includes('/onsite-approval-override/') ||
      url.includes('/notes-activity') ||
      url.includes('/accounting/')
    );
  }
}
